import { Info } from "lucide-react";
import { EhrChip } from "@/components/chat/SidebarChat/DocLink/EhrChip";
import { DocChip, DocChipCollapsed } from "@/components/chat/SidebarChat/DocLink/DocChip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { SourceGroup } from "./sourceGroups";
import { ehrUrlFor, isEhrRegistryName } from "./ehrUrl";

const REASONING_LABEL = "Why this value was chosen";
const REASONING_TRIGGER_CLASS =
  "inline-flex h-4 w-4 items-center justify-center rounded text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring";

/** Small ⓘ icon whose hover tooltip explains why a source produced its value. */
export function ReasoningInfoIcon({ reasoning }: { reasoning: string }) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={REASONING_LABEL}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className={REASONING_TRIGGER_CLASS}
          >
            <Info size={12} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm whitespace-pre-wrap text-xs">
          {reasoning}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * The same ⓘ, opened by click — the default everywhere, because a Radix tooltip
 * never opens on touch and the hover variant above therefore hides its text
 * completely on a tablet, where the reasoning is the whole point of the surface.
 *
 * The tooltip survives for one case only: a chip rendered inside a HoverCard,
 * which unmounts the moment the pointer leaves it and would take a popover down
 * with it. That is `SourceGroupChip`'s `insideHoverCard` prop.
 */
export function ReasoningInfoPopover({ reasoning }: { reasoning: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={REASONING_LABEL}
          // The chip sits inside FieldRow's row root, whose own onClick selects
          // the field — reading the reasoning would also open the inspector over
          // it. Stop the bubble only: Radix composes its toggle after this
          // handler and skips it on preventDefault, so the popover still opens.
          onClick={(e) => e.stopPropagation()}
          className={REASONING_TRIGGER_CLASS}
        >
          <Info size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        // The content is portalled to the body, but a React event still bubbles
        // through the React tree — so selecting the reasoning text would reach
        // the row root just as the trigger did. Stop it here as well.
        onClick={(e) => e.stopPropagation()}
        className="w-72 whitespace-pre-wrap p-3 text-xs leading-5"
      >
        {reasoning}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Renders a single grouped non-user source as a chip: the EHR registry link, or
 * a clickable document chip (extractor / IFC), with an optional reasoning ⓘ.
 * Shared by the field-row tooltip, the conflict resolver, the review dialog and
 * the value-history modal so all of them render sources identically (WHA-3720).
 */
export function SourceGroupChip({
  group,
  registryCode,
  insideHoverCard = false,
  onDocPreview,
}: {
  group: SourceGroup;
  registryCode: string | null | undefined;
  /**
   * The chip sits inside a HoverCard, so its reasoning has to be a tooltip: the
   * card unmounts on pointer-out and would close a popover with it. Everywhere
   * else the reasoning opens on click, or a tablet cannot read it at all.
   */
  insideHoverCard?: boolean;
  onDocPreview: (filename: string) => void;
}) {
  const ReasoningInfo = insideHoverCard ? ReasoningInfoIcon : ReasoningInfoPopover;
  if (group.kind === "ehr") {
    // Only Ehitisregister earns the chip and its deep link. `source_system` is
    // free text, so another registry can appear there — that one is named in
    // plain text, exactly as EvidenceSourceCard and FieldRow do.
    const registry = isEhrRegistryName(group.sourceSystem) ? (
      <EhrChip url={ehrUrlFor(registryCode)} className="h-5 px-1.5 text-[10px]" />
    ) : (
      <span className="text-[10px] font-medium text-muted-foreground">{group.sourceSystem}</span>
    );
    const ehrReasoning = group.reasoning?.trim();
    if (!ehrReasoning) return registry;
    return (
      <span className="inline-flex items-center gap-1">
        {registry}
        <ReasoningInfo reasoning={ehrReasoning} />
      </span>
    );
  }
  // IFC (BIM-model) + regular extractor sources render identically: a clickable
  // DocChip whose FileTypeIcon shows the right glyph (the violet BIM cube for
  // .ifc) and whose click opens the preview / 3D-model confirm via onDocPreview.
  const chip =
    group.filenames.length === 1 ? (
      <DocChip
        filename={group.filenames[0]}
        onClick={() => onDocPreview(group.filenames[0])}
        className="h-5 px-1.5 text-[10px]"
      />
    ) : (
      <DocChipCollapsed
        filenames={group.filenames}
        onDocClick={onDocPreview}
        className="h-5 px-1.5 text-[10px]"
      />
    );
  const reasoning = group.reasoning?.trim();
  if (!reasoning) return chip;
  return (
    <span className="inline-flex items-center gap-1">
      {chip}
      <ReasoningInfo reasoning={reasoning} />
    </span>
  );
}
